<template>
  <div class="d-flex align-items-center w-100">
    <div class="flex-grow-1 w-100" :class="{ 'select2-wrap-text': wrapText }">
      <select2
        :value="normalizedValue"
        :options="normalizedOptions"
        :placeholder="mergedSettings.placeholder"
        :settings="mergedSettings"
        :name="name"
        @select="setValue"
      />
    </div>

    <feather-icon
      v-if="hasValue"
      icon="XIcon"
      size="18"
      class="cursor-pointer ml-1 text-danger"
      @click="unsetValue"
    />
  </div>
</template>

<script>
import Select2 from 'v-select2-component';
import serviceDefaultConfig from '@core/auth/service/serviceDefaultConfig';

export default {
  components: { Select2 },
  props: {
    value: { default: null },
    placeholder: { type: String, default: '' },
    name: { type: String, default: '' },
    url: { type: String, default: '' },
    returnedKey: { type: String, default: '' },
    min: { default: null },
    max: { default: null },
    wrapText: { type: Boolean, default: false }
  },
  data() {
    return {
      settings: {}
    };
  },
  computed: {
    hasValue() {
      if (Array.isArray(this.value)) return this.value.length > 0;
      if (this.value && typeof this.value === 'object') return !!this.value.id;
      return this.value !== '' && this.value !== null && typeof this.value !== 'undefined';
    },
    normalizedValue() {
      if (
        this.value &&
        typeof this.value === 'object' &&
        Object.prototype.hasOwnProperty.call(this.value, 'id')
      ) {
        return this.value.id;
      }
      if (this.value === '' || this.value === null || typeof this.value === 'undefined') return [];
      return this.value;
    },
    normalizedOptions() {
      if (
        this.value &&
        typeof this.value === 'object' &&
        Object.prototype.hasOwnProperty.call(this.value, 'id')
      ) {
        return [this.value];
      }
      return [];
    },
    mergedSettings() {
      return {
        width: '100%',
        allowClear: false,
        placeholder: this.placeholder || this.$t('Make a Choice'),
        ...this.settings
      };
    }
  },
  created() {
    const that = this;

    this.settings = {
      ajax: {
        url: `${process.env.VUE_APP_API_URL}/${this.url}`,
        headers: {
          Authorization: `${serviceDefaultConfig.tokenType} ${localStorage.getItem(
            serviceDefaultConfig.storageTokenKeyName
          )}`
        },
        type: 'POST',
        contentType: 'application/json;charset=UTF-8',
        data: params =>
          JSON.stringify({
            search: params.term
          }),
        processResults: response => {
          if (response.status && response.data && response.data.length) {
            return {
              results: response.data.map(x => {
                const displayText = x.code ? `${x.code} - ${x.description}` : x.description;
                if (this.returnedKey) {
                  return {
                    id: x.code,
                    text: displayText,
                    gtipId: x.id
                  };
                }

                return {
                  id: x.id,
                  text: displayText,
                  code: x.code,
                  gtipId: x.id
                };
              }),
              pagination: {
                more: false
              }
            };
          }
          return {
            results: []
          };
        }
      },
      minimumInputLength: this.min || 3,
      maximumInputLength: this.max || 50,
      language: {
        inputTooShort(e) {
          return `${that.$t('Minimum number of characters')} ${
            e.minimum - parseInt(e.input.length)
          }`;
        },
        noResults() {
          return that.$t('No records found');
        },
        searching() {
          return that.$t('Searching');
        },
        loadingMore() {
          return that.$t('Loading more results');
        },
        errorLoading() {
          return that.$t('An error occurred');
        },
        inputTooLong(e) {
          return `${that.$t('Maximum number of characters')} ${e.maximum}`;
        }
      }
    };
  },
  methods: {
    setValue(val) {
      this.$emit('setValue', val);
    },
    unsetValue() {
      // keep backward-compat: consumers already treat falsy as cleared
      this.$emit('setValue', '');
    }
  }
};
</script>

<style scoped>
.w-100 :deep(.select2-container--default .select2-selection--single) {
  min-height: 38px;
}

.w-100 :deep(.select2-container--default .select2-selection--single .select2-selection__rendered) {
  line-height: 36px;
}

.w-100 :deep(.select2-container--default .select2-selection--single .select2-selection__arrow) {
  height: 36px;
}

.selected-company-name {
  border: 1px solid #aaaaaa;
  padding: 10px 13px;
  border-radius: 5px;
  margin-top: 8px;
}
body.dark-layout .selected-company-name {
  border-color: #3b4253 !important;
}
</style>

<style>
.select2-wrap-text .select2-container .select2-selection--single {
  height: auto !important;
  min-height: 38px !important;
}

.select2-wrap-text .select2-container .select2-selection--single .select2-selection__rendered {
  white-space: normal !important;
  word-break: break-word !important;
  text-overflow: clip !important;
  overflow: visible !important;
  line-height: 1.4 !important;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
  padding-right: 25px !important;
}

.select2-wrap-text .select2-container .select2-selection--single .select2-selection__arrow {
  height: auto !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
}
</style>
